import { useState, useRef, useEffect, useId } from "react";
import ReactDOM from "react-dom";

export interface SelectOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  /** "default" = main content area, "nav" = dark sidebar */
  variant?: "default" | "nav";
  /** Open the list upward instead of downward */
  upward?: boolean;
  style?: React.CSSProperties;
}

/**
 * Fully custom styled dropdown — replaces native <select> so the open list
 * can also be themed, not just the trigger button.
 *
 * The option list is portalled to document.body so it is never clipped by
 * an ancestor's overflow:hidden (e.g. card wrappers).
 */
export default function CustomSelect({
  value, onChange, options, placeholder, variant = "default", upward = false, style,
}: Props) {
  const [open, setOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const labelId = useId();

  // Capture trigger position whenever the dropdown opens
  useEffect(() => {
    if (open && containerRef.current) {
      setTriggerRect(containerRef.current.getBoundingClientRect());
    }
  }, [open]);

  // Close on outside click — must exclude both the trigger container AND the
  // portalled dropdown div, otherwise mousedown on an option unmounts it before
  // the click event fires and the onChange never gets called.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target) ?? false;
      const insideDropdown = dropdownRef.current?.contains(target) ?? false;
      if (!insideTrigger && !insideDropdown) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  // Recompute position on scroll / resize so the list tracks the trigger
  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (containerRef.current) setTriggerRect(containerRef.current.getBoundingClientRect());
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? placeholder ?? "";

  const isNav = variant === "nav";

  const triggerStyle: React.CSSProperties = isNav ? {
    width: "100%",
    padding: "0.4rem 2rem 0.4rem 0.7rem",
    fontSize: "0.82rem",
    background: "rgba(255,255,255,0.04)",
    color: "var(--color-nav-text)",
    border: "1px solid var(--color-border)",
    borderRadius: "10px",
    cursor: "pointer",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "relative",
    boxSizing: "border-box",
  } : {
    padding: "0.6rem 2.2rem 0.6rem 0.9rem",
    fontSize: "0.93rem",
    borderRadius: "12px",
    border: "1px solid var(--color-input-border)",
    background: "var(--color-input-bg)",
    color: "var(--color-input-text)",
    cursor: "pointer",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "relative",
    boxSizing: "border-box",
    width: "100%",
    ...style,
  };

  const dropdownStyle = (rect: DOMRect): React.CSSProperties => {
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUpward = upward || (spaceBelow < 200 && spaceAbove > spaceBelow);
    const maxH = openUpward
      ? Math.min(320, spaceAbove)
      : Math.min(320, spaceBelow);
    return {
      position: "fixed",
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      left: rect.left,
      width: Math.max(rect.width, 140),
      zIndex: 9999,
      background: isNav ? "var(--color-nav-bg)" : "var(--color-surface)",
      border: "1px solid var(--color-border)",
      borderRadius: "12px",
      boxShadow: "0 8px 32px var(--color-shadow)",
      overflowY: "auto",
      maxHeight: Math.max(maxH, 80),
    };
  };

  const optionStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: isNav ? "0.45rem 0.75rem" : "0.6rem 0.9rem",
    fontSize: isNav ? "0.82rem" : "0.9rem",
    cursor: "pointer",
    background: isSelected
      ? (isNav ? "rgba(255,255,255,0.08)" : "var(--color-surface-alt)")
      : "transparent",
    color: isSelected
      ? (isNav ? "var(--color-nav-text)" : "var(--color-accent)")
      : (isNav ? "var(--color-nav-text)" : "var(--color-text)"),
    fontWeight: isSelected ? 600 : 400,
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    userSelect: "none",
  });

  const optionList = open && triggerRect ? ReactDOM.createPortal(
    <div ref={dropdownRef} style={dropdownStyle(triggerRect)} role="listbox">
      {placeholder && (
        <div
          role="option"
          aria-selected={!value}
          onClick={() => { onChange(""); setOpen(false); }}
          style={optionStyle(!value)}
          onMouseEnter={(e) => {
            if (value) (e.currentTarget as HTMLDivElement).style.background = isNav ? "rgba(255,255,255,0.05)" : "var(--color-surface-alt)";
          }}
          onMouseLeave={(e) => {
            if (value) (e.currentTarget as HTMLDivElement).style.background = "transparent";
          }}
        >
          {!value && <span style={{ fontSize: "0.7rem", color: "var(--color-accent)", flexShrink: 0 }}>✓</span>}
          <span style={{ color: "var(--color-text-muted)", fontStyle: "italic" }}>{placeholder}</span>
        </div>
      )}
      {options.map((opt) => {
        const isSelected = opt.value === value;
        return (
          <div
            key={opt.value}
            role="option"
            aria-selected={isSelected}
            onClick={() => { onChange(opt.value); setOpen(false); }}
            style={optionStyle(isSelected)}
            onMouseEnter={(e) => {
              if (!isSelected) {
                (e.currentTarget as HTMLDivElement).style.background = isNav
                  ? "rgba(255,255,255,0.05)"
                  : "var(--color-surface-alt)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isSelected) {
                (e.currentTarget as HTMLDivElement).style.background = "transparent";
              }
            }}
          >
            {isSelected && (
              <span style={{ fontSize: "0.7rem", color: "var(--color-accent)", flexShrink: 0 }}>✓</span>
            )}
            {opt.label}
          </div>
        );
      })}
    </div>,
    document.body,
  ) : null;

  return (
    <div ref={containerRef} style={{ position: "relative", ...(!isNav ? style : {}) }} id={labelId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={isNav ? triggerStyle : { ...triggerStyle, ...style }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selectedLabel}
        </span>
        <span style={{
          display: "inline-block",
          transition: "transform 0.18s",
          transform: open ? (upward ? "rotate(0deg)" : "rotate(180deg)") : (upward ? "rotate(180deg)" : "rotate(0deg)"),
          fontSize: "0.65rem",
          opacity: 0.6,
          marginLeft: "0.4rem",
          flexShrink: 0,
        }}>
          ▾
        </span>
      </button>

      {optionList}
    </div>
  );
}
